/**
 * React Query Hook: Get Metadata Types
 *
 * Fetches metadata types (folders) with file counts for the selected organization
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { graphqlRequest } from "@/services/deployment/client";
import { GET_METADATA_TYPES_QUERY } from "@/services/metadata_v1/queries";
import type {
  GetMetadataTypesResponse,
  GetMetadataTypesVariables,
  MetadataType,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Fetch metadata types from API
 */
async function fetchMetadataTypes(
  variables: GetMetadataTypesVariables,
): Promise<MetadataType[]> {
  const response = await graphqlRequest<GetMetadataTypesResponse>(
    GET_METADATA_TYPES_QUERY,
    variables,
  );
  return response.metadataTypes;
}

/**
 * Hook to fetch metadata types (folders with file counts)
 *
 * Defaults to auth store org + metadata progress store retrieval (then org's latest retrieval).
 * Callers (e.g. dev-agent sidebar) may pass explicit `orgId` / `retrievalId` to stay in sync
 * with `effectiveRetrievalId` from the progress store.
 *
 * @param options - Query options
 * @returns React Query result with metadata types
 */
export function useMetadataTypes(
  options?: {
    enabled?: boolean;
    orgId?: string;
    retrievalId?: string | null;
  }
): UseQueryResult<MetadataType[], Error> {
  const { enabled = true } = options || {};

  // Subscribe to selected organization from auth store (this ensures re-render when store updates)
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const token = useAuthStoreV1((state) => state.token);

  // Extract orgId and retrievalId. Keep showing in-progress initial fetch results,
  // but hide partial folders once the initial fetch ends failed/cancelled —
  // unless initial retrieval previously completed (flag in localStorage), in which
  // case the CANCELLED status is from a subsequent sync cancel, not a first-time
  // failure, so the existing data is still valid.
  const orgId = options?.orgId ?? selectedOrg?.id;
  const latestRetrieval = selectedOrg?.latestMetadataRetrieval;
  const hasEverCompletedInitialFetch =
    orgId ? localStorage.getItem(`ncoder.initial_done.${orgId}`) === '1' : false;
  const defaultRetrievalId =
    !hasEverCompletedInitialFetch &&
    latestRetrieval?.retrievalType === 'INITIAL_RETRIEVAL' &&
    (latestRetrieval.status === 'FAILED' || latestRetrieval.status === 'CANCELLED')
      ? null
      : latestRetrieval?.id;
  const retrievalId = options?.retrievalId ?? defaultRetrievalId;

  const isEnabled = enabled && !!token && !!orgId && !!retrievalId;

  return useQuery({
    queryKey: ['metadataTypes', orgId, retrievalId],
    queryFn: () => fetchMetadataTypes({ orgId: orgId!, retrievalId: retrievalId! }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
